import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Mail, MapPin, Send, Instagram, Facebook, Twitter, Star } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // The form will be handled by Netlify Forms when deployed
    // For local development, we'll simulate success
    try {
      // Create FormData for Netlify
      const form = e.target;
      const data = new FormData(form);
      
      // Submit to Netlify Forms
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data).toString()
      });

      if (response.ok || response.status === 200) {
        setSubmitted(true);
        toast.success('Message sent successfully!', {
          description: 'We\'ll get back to you as soon as possible.'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      // Fallback: try to submit to backend API
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          setSubmitted(true);
          toast.success('Message sent successfully!');
          setFormData({ name: '', email: '', message: '' });
        } else {
          throw new Error('Backend submission failed');
        }
      } catch (backendError) {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - LuxuryStyle | Get in Touch</title>
        <meta 
          name="description" 
          content="Contact LuxuryStyle for questions about our waterproof jewellery, orders, or collaborations. We'd love to hear from you." 
        />
        <meta property="og:title" content="Contact Us - LuxuryStyle" />
        <meta property="og:description" content="Get in touch with our team." />
        <link rel="canonical" href="https://luxurystyle.info/contact" />
      </Helmet>

      <main data-testid="contact-page" className="pt-20">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-[#0a0a0a] border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
              Get in Touch
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mt-4 mb-4">
              Contact Us
            </h1>
            <div className="gold-divider mb-6" />
            <p className="text-white/60 max-w-2xl mx-auto">
              Have questions about our jewellery or need assistance with an order? 
              We're here to help. Reach out and we'll get back to you promptly.
            </p>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-20 md:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div>
                <h2 className="text-2xl md:text-3xl font-medium text-white mb-8">
                  Send Us a Message
                </h2>

                {submitted ? (
                  <div className="section-card p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] flex items-center justify-center">
                      <Send className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium text-white mb-4">Thank You!</h3>
                    <p className="text-white/60">
                      Your message has been sent successfully. We'll get back to you 
                      at the email address you provided.
                    </p>
                  </div>
                ) : (
                  <form
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                    className="space-y-6"
                  >
                    {/* Hidden field for Netlify Forms */}
                    <input type="hidden" name="form-name" value="contact" />
                    
                    {/* Honeypot field to prevent spam */}
                    <p className="hidden">
                      <label>
                        Don't fill this out if you're human: 
                        <input name="bot-field" />
                      </label>
                    </p>

                    {/* Name */}
                    <div>
                      <label 
                        htmlFor="name" 
                        className="block text-white/60 text-xs uppercase tracking-[0.2em] mb-3"
                      >
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name"
                        className="input-luxury"
                        placeholder="Your name"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label 
                        htmlFor="email" 
                        className="block text-white/60 text-xs uppercase tracking-[0.2em] mb-3"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email"
                        className="input-luxury"
                        placeholder="your@email.com"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label 
                        htmlFor="message" 
                        className="block text-white/60 text-xs uppercase tracking-[0.2em] mb-3"
                      >
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        data-testid="contact-message"
                        className="input-luxury resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="btn-luxury w-full flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {submitting ? (
                        <span>Sending...</span>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Info */}
              <div className="lg:pl-12">
                <h2 className="text-2xl md:text-3xl font-medium text-white mb-8">
                  Contact Information
                </h2>

                <div className="space-y-8">
                  {/* Email */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Email</h3>
                      <a 
                        href="mailto:Luxurystyle00@outlook.com"
                        data-testid="contact-info-email"
                        className="text-white/60 hover:text-[#D4AF37] transition-colors"
                      >
                        Luxurystyle00@outlook.com
                      </a>
                    </div>
                  </div>

                  {/* Instagram */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <Instagram className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Instagram</h3>
                      <a 
                        href="https://www.instagram.com/luxury_style_studioo?igsh=OWk0M2I1bnQwb3gz&utm_source=qr"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-[#D4AF37] transition-colors"
                      >
                        @luxury_style_studio
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Location</h3>
                      <p className="text-white/60">
                        United Kingdom
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="mt-12 pt-12 border-t border-white/10">
                  <h3 className="text-white font-accent text-xs uppercase tracking-[0.2em] mb-6">
                    Follow Us
                  </h3>
                  <div className="flex space-x-4">
                    <a
                      href="https://www.instagram.com/luxury_style_studioo?igsh=OWk0M2I1bnQwb3gz&utm_source=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-social-instagram"
                      className="w-12 h-12 border border-white/10 flex items-center justify-center text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5" strokeWidth={1.5} />
                    </a>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-social-facebook"
                      className="w-12 h-12 border border-white/10 flex items-center justify-center text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5" strokeWidth={1.5} />
                    </a>
                    <a
                      href="https://twitter.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-social-twitter"
                      className="w-12 h-12 border border-white/10 flex items-center justify-center text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                      aria-label="Twitter"
                    >
                      <Twitter className="w-5 h-5" strokeWidth={1.5} />
                    </a>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="mt-12 p-6 bg-[#111] border border-white/5">
                  <h3 className="text-white font-accent text-xs uppercase tracking-[0.2em] mb-4">
                    Response Time
                  </h3>
                  <p className="text-white/60 text-sm">
                    We typically respond within 24-48 hours during business days. 
                    For urgent inquiries, please indicate so in your message.
                  </p>
                </div>

                {/* Google Review CTA */}
                <div className="mt-8 p-6 bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="flex space-x-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" strokeWidth={1.5} />
                      ))}
                    </div>
                  </div>
                  <h3 className="text-white font-medium mb-2">Love Our Jewellery?</h3>
                  <p className="text-white/60 text-sm mb-4">
                    Share your experience and help others discover LuxuryStyle.
                  </p>
                  <a
                    href="https://share.google/jNF6BvocJeA38Cdry"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-google-review"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#F2D06B] transition-all"
                  >
                    <Star className="w-4 h-4" strokeWidth={1.5} />
                    <span>Leave a Google Review</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ContactPage;
